import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Tabs,
  Tab,
  TextField,
  Paper,
  Grid,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Tooltip,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AddBoxTwoToneIcon from '@mui/icons-material/AddBoxTwoTone';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import get from 'lodash/get';

import * as API from 'api/gateways';
import InputLabel from 'components/InputLabel';
import { createGateway, createPolicyObject, deleteGateway, updateGateway } from 'store/objects/actions';
import { OBJECT_CATEGORIES } from 'enum/object';
import { DEFAULT_LOCATION, LOCATION_TYPE } from 'enum/location';
import { GATEWAY_TYPE, MODEL_TYPE } from 'enum/gateway';
import { IP_MODES } from 'enum/ip';
import IPValidator, { normalizeIPv4Network } from 'utils/ipValidator';
import { generateKeypair } from 'utils/wireguardUtils';

import DescriptionTab from '../components/DescriptionTab';
import { validationSchema } from './validationSchema';

function checkMask(ip) {
  if (ip) {
    return ip.split('/')[1] ? ip : `${ip}/32`;
  }
  return null;
}

function removeMask(ip) {
  return ip.split('/')[0];
}

function normalizeNetwork(ip) {
  return normalizeIPv4Network(ip) || checkMask(ip);
}

const GatewayForm = ({ object, policyField, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();

  const addresses = useSelector((state) => get(state, `objects.${ecosystemUUID}.objects.addresses`, []));

  const [selectedTab, setSelectedTab] = useState(1);

  const isEditing = Boolean(object?.uuid);

  const handleSave = async (values) => {
    const acretoId = addresses.find((addr) => addr.name === 'Default Tunnel')?.address ?? '';
    let peerId = values.peer_id;

    if (values.gateway_type === GATEWAY_TYPE.WIREGUARD && !peerId) {
      const { publicKey } = generateKeypair();
      peerId = publicKey;
    } else if (values.model === MODEL_TYPE.AWS_SITE) {
      peerId = removeMask(values.peer_address);
    }

    const payload = {
      name: values.name,
      description: values.description,
      category: values.category,
      model: values.model,
      gateway_type: values.gateway_type,
      allow_all_ciphers: values.allow_all_ciphers,
      peer_address: removeMask(values.peer_address),
      peer_id: peerId,
      acreto_id: removeMask(acretoId),
      geolocation: {
        latitude: values.lat,
        longitude: values.lng,
      },
    };

    if (values.ipsec_fail_open) {
      payload.ipsec_fail_open = values.ipsec_fail_open;
    }
    if (values.gateway_internet_proto === IP_MODES.STATIC) {
      payload.gateway_internet_gw = values.gateway_internet_gw;
      payload.gateway_internet_ip = checkMask(values.gateway_internet_ip);
    }
    if (values.gateway_type === GATEWAY_TYPE.VGATEWAY) {
      payload.gateway_internet_proto = values.gateway_internet_proto;
    }
    if ([GATEWAY_TYPE.VGATEWAY, GATEWAY_TYPE.WIREGUARD].includes(values.gateway_type)) {
      payload.gateway_local_ip = checkMask(values.gateway_local_ip);
    }
    if (values.peer_port && Number(values.peer_port)) {
      payload.peer_port = Number(values.peer_port);
    }
    payload.peer_networks = values.peer_networks.map((net) => {
      if ([GATEWAY_TYPE.VGATEWAY, GATEWAY_TYPE.WIREGUARD].includes(values.gateway_type)) {
        return {
          network: normalizeNetwork(net.network),
          gateway: net.gateway,
        };
      }
      return { network: normalizeNetwork(net.network) };
    });

    if (values.peer_network) {
      if ([GATEWAY_TYPE.VGATEWAY, GATEWAY_TYPE.WIREGUARD].includes(values.gateway_type)) {
        payload.peer_networks.push({
          network: normalizeNetwork(values.peer_network),
          gateway: values.peer_gateway,
        });
      } else {
        payload.peer_networks.push({ network: normalizeNetwork(values.peer_network) });
      }
    }

    try {
      if (object?.uuid) {
        const result = await API.updateGateway({
          customerUUID,
          ecosystemUUID,
          payload,
          uuid: object.uuid,
        });
        dispatch(updateGateway({ ecosystemUUID, result }));
        enqueueSnackbar(`${values.name} (Gateway) has been updated`, { variant: 'success' });
      } else {
        const result = await API.createGateway({ customerUUID, ecosystemUUID, payload });
        result.element = 'gateway';
        dispatch(createGateway({ ecosystemUUID, result }));
        if (policyField) {
          dispatch(createPolicyObject({ field: policyField, object: result }));
        }
        enqueueSnackbar('New Gateway has been created', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      dispatch({ type: 'GATEWAY_FAILURE', payload: { message: error } });
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    isSubmitting,
  } = useFormik({
    initialValues: {
      name: get(object, 'name', ''),
      description: get(object, 'description', ''),
      category: get(object, 'category', ''),
      model: get(object, 'model', MODEL_TYPE.GENERIC),
      gateway_type: get(object, 'gateway_type', GATEWAY_TYPE.IPSEC),
      gateway_internet_gw: get(object, 'gateway_internet_gw', ''),
      gateway_internet_ip: get(object, 'gateway_internet_ip', ''),
      gateway_internet_proto: get(object, 'gateway_internet_proto', ''),
      gateway_local_ip: get(object, 'gateway_local_ip', ''),
      peer_address: get(object, 'peer_address', ''),
      peer_port: get(object, 'peer_port', ''),
      peer_id: get(object, 'peer_id', ''),
      peer_networks: get(object, 'peer_networks', []),
      peer_network: '',
      peer_gateway: '',
      allow_all_ciphers: get(object, 'allow_all_ciphers', true),
      ipsec_fail_open: get(object, 'ipsec_fail_open', false),
      ownPubKey: object?.gateway_type === GATEWAY_TYPE.WIREGUARD,

      // eslint-disable-next-line no-nested-ternary
      location: object
        ? object.geolocation
          ? LOCATION_TYPE.COORDINATES
          : LOCATION_TYPE.UNDEFINED
        : LOCATION_TYPE.COORDINATES,
      lat: get(object, 'geolocation.latitude', DEFAULT_LOCATION.LAT),
      lng: get(object, 'geolocation.longitude', DEFAULT_LOCATION.LNG),
    },
    validationSchema,
    onSubmit: handleSave,
  });

  useEffect(() => {
    setSelectedTab(1);
  }, [isSubmitting]);

  const handleDelete = async () => {
    if (!object?.uuid) return;
    try {
      await API.deleteGateway({ customerUUID, ecosystemUUID, uuid: object.uuid });
      dispatch(deleteGateway({ ecosystemUUID, uuid: object.uuid }));
      enqueueSnackbar(`${object.name} (Gateway) has been removed`, { variant: 'success' });
      onClose();
    } catch (error) {
      dispatch({ type: 'GATEWAY_DELETE_FAILURE', payload: { message: error } });
    }
  };

  const lastPeerNetworksIndex = values.peer_networks.length - 1;

  const handleAddPeerNetwork = () => {
    if (!values.peer_network && !values.peer_gateway) {
      return;
    }
    if (!IPValidator(values.peer_network, 4)) {
      setFieldTouched(`peer_network`, true);
      setFieldError('peer_network', 'Invalid IP');
      return;
    }
    if (values.peer_gateway && !IPValidator(values.peer_gateway, 4)) {
      setFieldTouched(`peer_gateway`, true);
      setFieldError('peer_gateway', 'Invalid IP');
      return;
    }
    const newNetworks = [
      ...values.peer_networks,
      { network: normalizeNetwork(values.peer_network), gateway: values.peer_gateway },
    ];
    setFieldValue('peer_networks', newNetworks);
    setFieldValue('peer_network', '');
    setFieldValue('peer_gateway', '');
    setFieldTouched(`peer_network`, false);
    setFieldTouched(`peer_gateway`, false);
  };

  const handleChangeType = (event, newType) => {
    if (!newType || isEditing) return;
    if (newType !== values.gateway_type) {
      setFieldValue('gateway_type', newType);
      setFieldValue('model', MODEL_TYPE.GENERIC);
      if (newType === GATEWAY_TYPE.VGATEWAY) {
        setFieldValue('gateway_internet_proto', IP_MODES.STATIC);
      }
      if (newType === GATEWAY_TYPE.IPSEC) {
        const newNetworks = [...values.peer_networks];
        newNetworks[lastPeerNetworksIndex].gateway = '';
      }
    }
  };

  const renderConfig = () => (
    <Grid container columnSpacing={12} rowSpacing={4}>
      <Grid item xs={12} lg={6}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <InputLabel required>Name</InputLabel>
            <TextField
              fullWidth
              name="name"
              variant="standard"
              placeholder="Name"
              value={values.name}
              onChange={handleChange}
              error={Boolean(touched.name && errors.name)}
              helperText={!!touched.name && errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel required>Category</InputLabel>
            <Autocomplete
              options={OBJECT_CATEGORIES}
              getOptionLabel={(option) => option.label}
              value={OBJECT_CATEGORIES.find((cat) => cat.value === values.category)}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <img src={option.icon} alt={option.label} style={{ width: 24, height: 24, marginRight: 8 }} />
                  {option.label}
                </Box>
              )}
              onChange={(event, newValue) => handleChange({ target: { name: 'category', value: newValue.value } })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    id: 'select-category_gateway',
                  }}
                  variant="standard"
                  placeholder="Select category"
                  error={Boolean(touched.category && errors.category)}
                  helperText={!!touched.category && errors.category}
                />
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ToggleButtonGroup
              color="primary"
              value={values.gateway_type}
              exclusive
              onChange={handleChangeType}
              name="gateway_type"
            >
              <ToggleButton id="toggle-vgateway" value={GATEWAY_TYPE.VGATEWAY}>
                vGateway
              </ToggleButton>
              <ToggleButton id="toggle-ipsec" value={GATEWAY_TYPE.IPSEC}>
                IPSec
              </ToggleButton>
              <ToggleButton id="toggle-wireguard" value={GATEWAY_TYPE.WIREGUARD}>
                WireGuard
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12}>
            <Stack>
              {values.gateway_type === GATEWAY_TYPE.IPSEC && (
                <>
                  <FormControl>
                    <FormLabel required id="model-label" sx={{ fontSize: 14 }}>
                      Model
                    </FormLabel>
                    <RadioGroup aria-labelledby="model-label" name="model" value={values.model} onChange={handleChange}>
                      <FormControlLabel
                        value={MODEL_TYPE.GENERIC}
                        control={<Radio size="small" />}
                        label="Generic IPsec"
                      />
                      <FormControlLabel
                        value={MODEL_TYPE.AWS_SITE}
                        control={<Radio size="small" />}
                        label="AWS site-to-site VPN"
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
              {[GATEWAY_TYPE.VGATEWAY, GATEWAY_TYPE.IPSEC].includes(values.gateway_type) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FormControl>
                    <FormControlLabel
                      sx={{ mb: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          name="ipsec_fail_open"
                          checked={values.ipsec_fail_open}
                          onChange={(e) => setFieldValue('ipsec_fail_open', e.target.checked)}
                        />
                      }
                      label={`${values.gateway_type === GATEWAY_TYPE.IPSEC ? 'IPSec' : 'vGateway'} Fail Open`}
                    />
                  </FormControl>
                  <Tooltip
                    slotProps={{ tooltip: { sx: { bgcolor: 'white', border: '1px solid gray' } } }}
                    title={
                      <Typography sx={{ fontSize: 12 }}>
                        Default: Disabled
                        <br />
                        When the tunnel is down Fail Open allows accessing the Internet directly using the default
                        gateway, bypassing Acreto
                      </Typography>
                    }
                  >
                    <IconButton sx={{ p: 0 }}>
                      <HelpCenterOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider variant="fullWidth" />
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={4}>
          {values.gateway_type === GATEWAY_TYPE.IPSEC && (
            <Grid item xs={12}>
              <InputLabel required={values.model === MODEL_TYPE.AWS_SITE}>
                {values.model === MODEL_TYPE.GENERIC
                  ? 'Allow connections from (optional)'
                  : 'AWS Tunnel Outside IP Address'}
              </InputLabel>
              <TextField
                fullWidth
                name="peer_address"
                variant="standard"
                placeholder="IP address"
                value={values.peer_address}
                onChange={handleChange}
                error={Boolean(touched.peer_address && errors.peer_address)}
                helperText={!!touched.peer_address && errors.peer_address}
              />
            </Grid>
          )}
          {values.gateway_type === GATEWAY_TYPE.VGATEWAY && (
            <>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  color="primary"
                  value={values.gateway_internet_proto}
                  exclusive
                  onChange={(e, newValue) => setFieldValue('gateway_internet_proto', newValue)}
                  name="gateway_internet_proto"
                >
                  <ToggleButton value={IP_MODES.DHCP}>DHCP</ToggleButton>
                  <ToggleButton value={IP_MODES.STATIC}>Static</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {values.gateway_internet_proto === IP_MODES.STATIC && (
                <>
                  <Grid item xs={12}>
                    <InputLabel required>vGateway Internet IP</InputLabel>
                    <TextField
                      fullWidth
                      name="gateway_internet_ip"
                      variant="standard"
                      placeholder="Static IP / 32"
                      value={values.gateway_internet_ip}
                      onChange={handleChange}
                      error={Boolean(touched.gateway_internet_ip && errors.gateway_internet_ip)}
                      helperText={!!touched.gateway_internet_ip && errors.gateway_internet_ip}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel required>vGateway Default Route</InputLabel>
                    <TextField
                      fullWidth
                      name="gateway_internet_gw"
                      variant="standard"
                      placeholder="Enter Default Route"
                      value={values.gateway_internet_gw}
                      onChange={handleChange}
                      error={Boolean(touched.gateway_internet_gw && errors.gateway_internet_gw)}
                      helperText={!!touched.gateway_internet_gw && errors.gateway_internet_gw}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <InputLabel required>vGateway Local IP</InputLabel>
                <TextField
                  fullWidth
                  name="gateway_local_ip"
                  variant="standard"
                  placeholder="Local IP / 24"
                  value={values.gateway_local_ip}
                  onChange={handleChange}
                  error={Boolean(touched.gateway_local_ip && errors.gateway_local_ip)}
                  helperText={!!touched.gateway_local_ip && errors.gateway_local_ip}
                />
              </Grid>
            </>
          )}
          {values.gateway_type === GATEWAY_TYPE.WIREGUARD && (
            <>
              <Grid item xs={12}>
                {!isEditing && (
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          name="ownPubKey"
                          checked={values.ownPubKey}
                          onChange={(e) => setFieldValue('ownPubKey', e.target.checked)}
                        />
                      }
                      label="I have WireGuard Client Public Key"
                    />
                  </FormControl>
                )}
                {values.ownPubKey && (
                  <>
                    <InputLabel>WireGuard Client Public Key</InputLabel>
                    <TextField
                      fullWidth
                      name="peer_id"
                      variant="standard"
                      placeholder="eAnMqNhZ7qi8/u9n6ZXxe+sA+JyIMTemy5lWZZfuwGU="
                      value={values.peer_id}
                      onChange={handleChange}
                      error={Boolean(touched.peer_id && errors.peer_id)}
                      helperText={!!touched.peer_id && errors.peer_id}
                    />
                  </>
                )}
              </Grid>
              <Grid item xs={12}>
                <InputLabel required>WireGuard Client Local IP</InputLabel>
                <TextField
                  fullWidth
                  name="gateway_local_ip"
                  variant="standard"
                  placeholder="Local IP / 24"
                  value={values.gateway_local_ip}
                  onChange={handleChange}
                  error={Boolean(touched.gateway_local_ip && errors.gateway_local_ip)}
                  helperText={!!touched.gateway_local_ip && errors.gateway_local_ip}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={4}>
          {values.gateway_type === GATEWAY_TYPE.VGATEWAY && (
            <Grid item xs={12}>
              <InputLabel>Allow connections from (optional)</InputLabel>
              <TextField
                fullWidth
                name="peer_address"
                variant="standard"
                placeholder="IP address"
                value={values.peer_address}
                onChange={handleChange}
                error={Boolean(touched.peer_address && errors.peer_address)}
                helperText={!!touched.peer_address && errors.peer_address}
              />
            </Grid>
          )}
          {values.gateway_type === GATEWAY_TYPE.WIREGUARD && (
            <>
              <Grid item xs={12}>
                <InputLabel>WireGuard Client Public IP</InputLabel>
                <Grid container columnSpacing={1}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      name="peer_address"
                      variant="standard"
                      placeholder="IP address"
                      value={values.peer_address}
                      onChange={handleChange}
                      error={Boolean(touched.peer_address && errors.peer_address)}
                      helperText={!!touched.peer_address && errors.peer_address}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      name="peer_port"
                      variant="standard"
                      placeholder="Port"
                      value={values.peer_port}
                      onChange={handleChange}
                      error={Boolean(touched.peer_port && errors.peer_port)}
                      helperText={!!touched.peer_port && errors.peer_port}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={10}>
            <InputLabel required>Local Networks</InputLabel>
            <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
              <Grid container spacing={0.5}>
                <Grid item xs={values.gateway_type === GATEWAY_TYPE.IPSEC ? 10 : 5}>
                  <InputLabel>Network</InputLabel>
                </Grid>
                {values.gateway_type !== GATEWAY_TYPE.IPSEC && (
                  <Grid item xs={7}>
                    <InputLabel>Next Hop</InputLabel>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider variant="fullWidth" />
                </Grid>
                {values.peer_networks.map((network, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={values.gateway_type === GATEWAY_TYPE.IPSEC ? 10 : 5}>
                      <Typography
                        sx={{ color: 'text.secondary', mt: '6px', ml: '4px' }}
                        data-testid={`peer_network_${index + 1}_network`}
                      >
                        {network.network}
                      </Typography>
                    </Grid>
                    {values.gateway_type !== GATEWAY_TYPE.IPSEC && (
                      <Grid item xs={5}>
                        <Typography
                          sx={{ color: 'text.secondary', mt: '6px', ml: '4px' }}
                          data-testid={`peer_network_${index + 1}_gateway`}
                        >
                          {network.gateway}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={2}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newNetworks = [...values.peer_networks];
                          newNetworks.splice(index, 1);
                          setFieldValue('peer_networks', newNetworks);
                        }}
                        sx={{ color: 'error.main' }}
                        data-testid={`btn-remove_peer_network_${index + 1}`}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider variant="fullWidth" />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={values.gateway_type === GATEWAY_TYPE.IPSEC ? 10 : 5}>
                  <TextField
                    fullWidth
                    variant="standard"
                    name="peer_network"
                    data-testid="input-peer_network"
                    placeholder="Network"
                    value={values.peer_network}
                    onChange={handleChange}
                    error={Boolean(touched.peer_network && errors.peer_network)}
                    helperText={!!touched.peer_network && errors.peer_network}
                  />
                </Grid>
                {values.gateway_type !== GATEWAY_TYPE.IPSEC && (
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      variant="standard"
                      name="peer_gateway"
                      data-testid="input-peer_gateway"
                      placeholder="Next Hop"
                      value={values.peer_gateway}
                      onChange={handleChange}
                      error={Boolean(touched.peer_gateway && errors.peer_gateway)}
                      helperText={!!touched.peer_gateway && errors.peer_gateway}
                    />
                  </Grid>
                )}
                <Grid item xs={2}>
                  <IconButton
                    size="small"
                    onClick={handleAddPeerNetwork}
                    sx={{ color: 'primary.main' }}
                    data-testid="btn-add_peer_network"
                  >
                    <AddBoxTwoToneIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
            {Boolean(touched.peer_networks && errors.peer_networks) && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.peer_networks}
              </Typography>
            )}
          </Grid>
          {[GATEWAY_TYPE.IPSEC, GATEWAY_TYPE.VGATEWAY].includes(values.gateway_type) && (
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControl>
                  <FormControlLabel
                    sx={{ mb: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        id="allow_all_ciphers"
                        name="allow_all_ciphers"
                        checked={values.allow_all_ciphers}
                        onChange={(e) => setFieldValue('allow_all_ciphers', e.target.checked)}
                      />
                    }
                    label="Allow all supported ciphers"
                  />
                </FormControl>
                <Tooltip
                  slotProps={{ tooltip: { sx: { bgcolor: 'white', border: '1px solid gray' } } }}
                  title={<Typography sx={{ fontSize: 12 }}>This may have performance impact</Typography>}
                >
                  <IconButton sx={{ p: 0 }}>
                    <HelpCenterOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ px: 4, pt: 2, pb: 3 }}>
      <Tabs value={selectedTab} onChange={(_, val) => setSelectedTab(val)} sx={{ mb: 2.5 }}>
        <Tab
          label="Configuration"
          data-testid="tab_configuration"
          value={1}
          sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
        />
        <Tab
          label="Descriptors"
          data-testid="tab_descriptors"
          value={2}
          sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
        />
      </Tabs>
      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 4 }}>
          {selectedTab === 1 && renderConfig()}
          {selectedTab === 2 && (
            <DescriptionTab values={values} errors={errors} touched={touched} onChange={handleChange} />
          )}
        </Box>
      </Paper>
      <Box sx={{ pt: 3 }}>
        <Stack direction="row-reverse" justifyContent="space-between">
          <Button data-testid="btn-save_gateway" variant="contained" onClick={handleSubmit} sx={{ minWidth: 90 }}>
            {object?.uuid ? 'Save' : 'Add'}
          </Button>
          {object?.uuid && (
            <Button
              data-testid="btn-delete_gateway"
              variant="contained"
              color="error"
              onClick={handleDelete}
              sx={{ minWidth: 90 }}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

GatewayForm.propTypes = {
  object: PropTypes.object,
  policyField: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default GatewayForm;
